import React, { useEffect, useState } from "react";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import AppInput from "components/Input/AppInput";
import { Box, FormGroup, makeStyles, Typography } from "@material-ui/core";
import { YupValidations } from "utils/validate";
import AppColor from "utilities/AppColor";
import AppButton from "components/CustomButtons/AppButton";
import SelectField from "components/Select/SelectField";
import { GENDER } from "config/AppConfig";
import AvatarUpload from "components/UploadFile/AvatarUpload";
import CustomDatePicker from "components/Input/CustomDatePicker";
import InputAutocompleteAddress from "components/Input/InputAutocompleteAddress";
import moment from "moment-timezone";
import AppConfig from "config/AppConfig";

const { PATTERNS } = AppConfig;
const styles = makeStyles(() => ({
  container: {
    '& hr': {
      borderTop: '1px solid #00000080'
    }
  },
  title: {
    display: 'flex',
    margin: '1rem 0 1rem 0'
  },
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    '& a': {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: AppColor.blue
    }
  }
}));

const maskNumber = '999999999999999';
const maskNumber10 = '9999999999';
const GeneralInformationForm = (props) => {
  const {
    onSubmit,
    isSubmitting,
    workerDetail,
    changeProgressStatus,
    display,
    disabled,
    isUpdate,
    isCreate
  } = props;

  const { t } = useTranslation();
  const classes = styles();

  const genderOptions = [
    {
      value: GENDER.MALE.id,
      label: t(GENDER.MALE.label)
    },
    {
      value: GENDER.FEMALE.id,
      label: t(GENDER.FEMALE.label)
    },
    {
      value: GENDER.OTHER.id,
      label: t(GENDER.OTHER.label)
    }
  ];

  const [initForm, setInitForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    postalCode: '',
    phone: '',
    dateOfBirth: '',
    gender: genderOptions[0].value,
    description: ''
  });
  const [latLng, setLatLng] = useState();

  const {
    id,
    email,
    firstName,
    lastName,
    address1,
    address2,
    dateOfBirth,
    postalCode,
    gender,
    phone,
    description,
    avatarAws3Link = ''
  } = workerDetail;

  const {
    lat,
    lng
  } = latLng || {};

  const validationSchema = Yup.object().shape({
    email: !id && Yup.string().trim()
      .required('validation.email.required')
      .email('validation.email.invalid')
      .max(255, { message: 'validation.max_length', value: 255})
      .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    firstName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    lastName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    address1: Yup.string().max(240, "validation.max_length_240"),
    address2: Yup.string().max(240, "validation.max_length_240"),
    description: Yup.string().max(1000, "validation.max_length_1000"),
    dateOfBirth: Yup.string()
    .test('dateOfBirth', "validation.max_date_of_birth", (dateOfBirth) => {
      if (!dateOfBirth || dateOfBirth === '') {
        return true;
      }
      if (moment(dateOfBirth).diff(moment(new Date().setHours(0,0,0,0))) >= 0) {
        return false;
      } else {
        return true;
      }
    }),
    postalCode: Yup.number()
    .test('len', "validation.max_length_10", val =>  {
      if (val || val === 0) {
        return Math.ceil(Math.log10(val + 1)) <= 10
      }
      return true;
    }),
    // gender: Yup.string().required("validation.required"),
    phone: YupValidations.phone
  });

  const validationSchemaUpdate = Yup.object().shape({
    email: !id && Yup.string().trim()
    .required('validation.email.required')
    .email('validation.email.invalid')
    .max(255, { message: 'validation.max_length', value: 255})
    .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
    avatar: Yup.mixed().required("validation.requiredAvatar"),
    firstName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    lastName: YupValidations.requiredString.max(35, "validation.max_length_35"),
    address1: YupValidations.requiredString.test('address1', 'validation.select_address_suggested', (value) => {
      if (value !== address1 && !latLng) {
        return false;
      }
      return true;
    }),
    address2: Yup.string().max(240, "validation.max_length_240"),
    description: YupValidations.requiredString.max(1000, "validation.max_length_1000"),
    dateOfBirth: Yup.string().required("validation.required")
    .test('dateOfBirth', "validation.max_date_of_birth", (dateOfBirth) => {
      if (moment(dateOfBirth).diff(moment(new Date().setHours(0,0,0,0))) >= 0) {
        return false;
      } else {
        return true;
      }
    }),
    postalCode: YupValidations.postalCode,
    gender: Yup.string().required("validation.required"),
    phone: YupValidations.phone
  });

  useEffect(() => {
    if (workerDetail && Object.keys(workerDetail).length > 0) {
      const initial = {
        email,
        firstName,
        lastName,
        address1,
        address2,
        dateOfBirth,
        postalCode,
        gender,
        phone,
        description,
        avatar: avatarAws3Link && avatarAws3Link !== '' ? avatarAws3Link : null
      };
      const checkValidationSchema = {
        email,
        firstName,
        lastName,
        address1,
        address2,
        dateOfBirth,
        postalCode: !postalCode || postalCode === '' ? 0 : postalCode,
        gender,
        phone,
        description,
        avatar: avatarAws3Link && avatarAws3Link !== '' ? avatarAws3Link : null
      };
      if (id) {
        validationSchemaUpdate
        .isValid(checkValidationSchema)
        .then((valid) => {
          setTimeout(() => {
            changeProgressStatus(0, valid);
          }, 100);
        });
      } else {
        validationSchema
        .isValid(checkValidationSchema)
        .then((valid) => {
          setTimeout(() => {
            changeProgressStatus(0, valid);
          }, 100);
        });
      }
      setInitForm(initial);
    }
  }, [workerDetail]);

  // const onUploadImage = (imageFile, localImageUrl) => {
  //   if (imageFile) {
  //     setAvatarPreview(localImageUrl);
  //     setAvatarFile(imageFile);
  //   }
  // };

  const handleSubmitForm = (form) => {
    // eslint-disable-next-line no-unused-vars
    const dateOfBirth = form.dateOfBirth && form.dateOfBirth !== '' ? form.dateOfBirth : null
    const contentData = { ...form, id, dateOfBirth };
    if (form.address1 !== address1 && latLng) {
      contentData.lat = lat;
      contentData.lng = lng;
    }
    const params = {
      contentData: contentData
    };
    if (form.avatar && form.avatar.name) {
      params.file = form.avatar
    }
  
    onSubmit(params);
  };


  return (
    <Formik
      enableReinitialize={true}
      onSubmit={handleSubmitForm}
      initialValues={initForm}
      validationSchema={isUpdate ? validationSchemaUpdate : validationSchema}
    >
      {({setFieldValue}) => {
        return (
          <Form className={`${classes.container} ${!display && 'hidden'}`}>
            {
              isCreate
              ? <>
                <Field
                  component={AppInput}
                  name="email"
                  id="email"
                  label={"label.email"}
                  required
                />
                <Field
                  component={AppInput}
                  name="firstName"
                  id="firstName"
                  label={"label.firstName"}
                  required
                />
                <Field
                  component={AppInput}
                  name="lastName"
                  id="lastName"
                  label={"label.lastName"}
                  required
                />
                <Field
                  component={AppInput}
                  name="phone"
                  type="tel"
                  mask={maskNumber}
                  maskChar=""
                  id="phone"
                  label={"label.phone"}
                  required
                />
              </>
              : <>
                  <Field
                    component={AvatarUpload}
                    name="avatar"
                    id="avatar"
                    disabled={disabled}
                  />
                  {/* <AvatarUpload
                    onUploadFile={onUploadImage}
                    preview={avatarPreview}
                    disabled={disabled}
                  /> */}
                  <Box className={classes.title}>
                    <Typography variant="h5">
                      {t("title.personalInformation")}
                    </Typography>
                  </Box>
                  <Field
                    component={AppInput}
                    name="email"
                    id="email"
                    label={"label.email"}
                    required
                    disabled={!!id || disabled}
                  />
                  <Field
                    component={AppInput}
                    name="firstName"
                    id="firstName"
                    label={"label.firstName"}
                    required
                    disabled={disabled}
                  />
                  <Field
                    component={AppInput}
                    name="lastName"
                    id="lastName"
                    label={"label.lastName"}
                    required
                    disabled={disabled}
                  />
                  <hr />
                  <Box className={classes.title}>
                    <Typography variant="h5">
                      {t("title.contactInformation")}
                    </Typography>
                  </Box>
                  <Field
                    component={InputAutocompleteAddress}
                    name="address1"
                    id="address1"
                    label={"label.address1"}
                    onChangePostalCode={postalcode => setFieldValue("postalCode", postalcode)}
                    onChangeLatLng={latlng => setLatLng(latlng)}
                    setLatLng={setLatLng}
                    disabled={disabled}
                    required={isUpdate}
                  />
                  <Field
                    component={AppInput}
                    name="address2"
                    id="address2"
                    label={"label.address2"}
                    disabled={disabled}
                  />
                  <Field
                    component={AppInput}
                    name="postalCode"
                    type="tel"
                    mask={maskNumber10}
                    maskChar=""
                    label={"label.postalCode"}
                    disabled={disabled}
                    required={isUpdate}
                  />
                  <Field
                    component={AppInput}
                    name="phone"
                    type="tel"
                    mask={maskNumber}
                    maskChar=""
                    id="phone"
                    label={"label.phone"}
                    required
                    disabled={disabled}
                  />
                  <Field
                    component={CustomDatePicker}
                    name="dateOfBirth"
                    type="text"
                    id="dateOfBirth"
                    label={"label.dateOfBirth"}
                    formatDateJob={true}
                    custom={{
                      openTo: "year",
                      views: ["year", "month", "date"],
                      maxDate: new Date(moment(new Date().setDate(new Date().getDate() - 1)))
                    }}
                    disabled={disabled}
                    required={isUpdate}
                  />
                  <Field
                    component={SelectField}
                    name="gender"
                    label={"label.gender"}
                    options={genderOptions}
                    placeholder={t("label.gender")}
                    disabled={disabled}
                    required={isUpdate}
                  />
                  <hr />
                  <Field
                    component={AppInput}
                    name="description"
                    type="textarea"
                    label={"label.bio"}
                    required={isUpdate}
                    disabled={disabled}
                  />
            
                </>
            }
            {
              !disabled && (
                <FormGroup className={classes.control}>
                  <AppButton
                    label={`${t("button.saveAndContinue")}`}
                    rounder
                    type="submit"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </FormGroup>
              )
            }
          </Form>
        );
      }}
    </Formik>
  );
};

GeneralInformationForm.propTypes = {
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  disabled: PropTypes.bool,
  display: PropTypes.bool,
  workerDetail: PropTypes.object,
  type: PropTypes.string,
  changeProgressStatus: PropTypes.func,
  isUpdate: PropTypes.bool,
  isCreate: PropTypes.bool
};

export default GeneralInformationForm;
